/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  Divider,
} from "@mui/material";
import {
  StyledTitleContainer,
  StyledOrderTitle,
  StyledIconButton,
  StyledOrderSubTitle,
  StyledInfoText,
  OrderDetailContainer,
  OrderInformation,
} from "./styles";
import ClearIcon from '@mui/icons-material/Clear';
import { ModalTable } from "../modal_table";
import camelize from "../../utils/camelize";
import {capitalize} from 'lodash'
import  { Loader } from '../loader/index'
import { formatDate } from "../../utils/formatDate";
import { BucketPill } from "../../pages/reports/perq_report/styles";

const DISCOUNT_CLASSIFICATION_LABELS = {
  managed: 'Managed',
  unmanaged: 'Unmanaged',
  unclassified: 'Unclassified',
}

export const OrderAppliedDiscountModal = ({ open, handleClose, ordersData, isLoading, isError = false }) => {

  const formatDiscountName = (record) => {
    if (record?.discount_percent){
      return `${record?.name} (${record?.discount_percent})`
    }
    return `${record?.name}`
  }

  const formatDiscountClassification = (record) => {
    return DISCOUNT_CLASSIFICATION_LABELS[record?.classification] || capitalize(record?.classification)
  }

  const itemTableColumns = [
    { key: 'menu_item', sortable: false },
    { key: 'modifiers', sortable: false },
    { key: 'price', sortable: false },
    { key: 'qty', sortable: false },
    { key: 'discount', label: 'Perq', sortable: false },
    { key: 'net', sortable: false },
    { key: 'tax', sortable: false},
    { key: 'total', sortable: false },
    { key: 'voided?', sortable: false },
    { key: 'reason', sortable: false },
    { key: 'refund_qty', sortable: false },
    { key: 'refund', sortable: false },
  ]

  const discountTableColumns = [
    { key: 'name', sortable: false },
    { key: 'classification', sortable: false },
    { key: 'amount', sortable: false },
    { key: 'applied_date', sortable: false },
    { key: 'approver', sortable: false },
    { key: 'reason', sortable: false },
    { key: 'comment', sortable: false },
  ]

  const serviceChargesTableColumns = [
    { key: 'name', sortable: false },
    { key: 'gratuity', sortable: false },
    { key: 'amount', sortable: false },
    { key: 'tax', sortable: false },
    { key: 'refund', sortable: false },
  ]

  const paymentTableColumns = [
    { key: 'payment', sortable: false },
    { key: 'date', sortable: false },
    { key: 'amount', sortable: false },
    { key: 'tip', sortable: false },
    { key: 'gratuity', sortable: false },
    { key: 'total', sortable: false },
    { key: 'refund', sortable: false },
    { key: 'status', sortable: false },
  ]

  const tableColumns = (columnList) => {
    const column = columnList?.map((column) => {
      return {
        key: column.key,
        label: column.label || camelize(column.key),
        sortable: column.sortable,
      }
    })
    return column
  }

  const itemTableRows = (data) => {
    return data?.map((record) => {
      return {
        menu_item: record?.menu_item,
        modifiers: record?.modifiers,
        price: `$${Number(record?.price).toFixed(2) ?? '0.00'}`,
        qty: record?.quantity,
        discount: `$${Number(record?.discount).toFixed(2) ?? '0.00'}`,
        net: `$${Number(record?.net).toFixed(2) ?? '0.00'}`,
        tax: `$${Number(record?.tax).toFixed(2) ?? '0.00'}`,
        total: `$${Number(record?.total).toFixed(2) ?? '0.00'}`,
        'voided?': record?.voided ? 'True' : 'False',
        reason: record?.reason,
        refund_qty: record?.refund_quantity,
        refund: `$${Number(record?.refund).toFixed(2) ?? '0.00'}`,
      };
    });
  };

  const discountTableRows = (data) => {
    return data?.map((record, index) => {
      return {
        name: formatDiscountName(record),
        classification: {
          display: (
            <BucketPill
              bucketType={record?.classification}
              label={formatDiscountClassification(record)}
              data-testid={`order-discount-classification-${index}-${record?.classification}`}
            />
          ),
        },
        amount: `$${Number(record?.amount).toFixed(2) ?? '0.00'}`,
        applied_date: formatDate(record?.applied_date, ordersData?.time_zone_abbr),
        approver: record?.approver,
        reason: record?.reason,
        comment: record?.comment,
      };
    });
  }

  const serviceChargesTableRows = (data) => {
    return data?.map((record) => {
      return {
        name: record?.name,
        gratuity: record?.gratuity,
        amount: `$${Number(record?.amount).toFixed(2) ?? '0.00'}`,
        tax: `$${Number(record?.tax).toFixed(2) ?? '0.00'}`,
        refund: `$${Number(record?.refund).toFixed(2) ?? '0.00'}`,
      };
    });
  }

  const paymentTableRows = (data) => {
    return data?.map((record) => {
      return {
        payment: record?.payment,
        date: formatDate(record?.date, ordersData?.time_zone_abbr),
        amount: `$${Number(record?.amount).toFixed(2) ?? '0.00'}`,
        tip: `$${Number(record?.tip).toFixed(2) ?? '0.00'}`,
        gratuity: `$${Number(record?.gratuity).toFixed(2) ?? '0.00'}`,
        total: `$${Number(record?.total).toFixed(2) ?? '0.00'}`,
        refund: `$${Number(record?.refund).toFixed(2) ?? '0.00'}`,
        status: record?.status,
      };
    });
  }

  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth={"md"}
        PaperProps={{
        style: {
          maxHeight: '70vh',
        },
        'data-testid': 'order-details-show-modal',
      }}
        data-testid="order-detail-modal"
      >
        <StyledTitleContainer data-testid="order-detail-modal-title">
          <StyledOrderTitle>
            Order Details
          </StyledOrderTitle>
          <StyledIconButton onClick={handleClose} disableRipple>
            <ClearIcon data-testid="order-detail-modal-close-icon"/>
          </StyledIconButton>
        </StyledTitleContainer>

        <Divider />
        {
          isLoading ? (
            <Loader />
          ) : isError ? (
            <StyledOrderSubTitle
              data-testid="order-detail-modal-error"
              sx={{margin: '20px', fontWeight: '500'}}
            >
              Failed to load order details
            </StyledOrderSubTitle>
          ) : (
            ordersData?.length === 0 ? (
              <StyledOrderSubTitle sx={{margin: '20px', fontWeight: '500'}}>No associated order</StyledOrderSubTitle>
            ) : (
              <DialogContent
                sx={{
                  overflowY: 'scroll',
                  scrollbarWidth: 'none',
                  padding: '30px',
                  '&::-webkit-scrollbar': { display: 'none' }
                }}
              >
                <div>
                  <StyledOrderSubTitle variant="h4">{`Order #${ordersData?.order_no} (${capitalize(ordersData?.order_status)})`}</StyledOrderSubTitle>
                  <StyledInfoText sx={{color: '#7a7a7a'}}>GUID: {ordersData?.order_guid}</StyledInfoText>

                  <OrderDetailContainer>
                    <OrderInformation label={'Guests:'} value={ordersData?.guests} />
                    <OrderInformation label={'Checks:'} value={`${ordersData?.check} (${ordersData?.check_no || ''})`} />
                  </OrderDetailContainer>

                  <OrderDetailContainer>
                    <OrderInformation label={'Source:'} value={ordersData?.source} />
                  </OrderDetailContainer>

                  <OrderDetailContainer>
                    <OrderInformation label={'Origin Device:'} value={ordersData?.origin_device} />
                    <OrderInformation label={'Most Recent Device:'} value={ordersData?.most_recent_device} />
                  </OrderDetailContainer>
                </div>
                <Divider sx={{marginTop: "15px"}}/>
                {
                  ordersData?.checks.length > 0 && (
                    ordersData?.checks?.map((check) => {
                      return (
                        <div key={check.check_guid} style={{marginTop: "20px"}}>
                          <StyledOrderSubTitle variant="h4">{`Check #${check?.check_id} (${capitalize(check?.status)})`}</StyledOrderSubTitle>
                          <StyledInfoText sx={{color: '#7a7a7a'}}>GUID: {check?.check_guid}</StyledInfoText>
                          <OrderDetailContainer>
                            <OrderInformation label={'Time Opened:'} value={formatDate(check?.time_opened, ordersData?.time_zone_abbr)} />
                            <OrderInformation label={'Perqs:'} value={`$${Number(check?.discount).toFixed(2) ?? '0.00'}`} />
                            <OrderInformation label={'Total:'} value={`$${Number(check?.total).toFixed(2) ?? '0.00'}`} />
                          </OrderDetailContainer>

                          <OrderDetailContainer>
                            <OrderInformation label={'Server:'} value={check?.server} />
                            <OrderInformation label={'Credits:'} value={`$${Number(check?.credits).toFixed(2) ?? '0.00'}`} />
                            <OrderInformation label={'Balance Due:'} value={`$${Number(check?.balance_due).toFixed(2) ?? '0.00'}`} />
                          </OrderDetailContainer>

                          <OrderDetailContainer>
                            <OrderInformation label={'Opened By Server:'} value={check?.opened_by_server} />
                            <OrderInformation label={'Sub Total:'} value={`$${Number(check?.sub_total).toFixed(2) ?? '0.00'}`} />
                            <OrderInformation label={'Tip:'} value={`$${Number(check?.tip).toFixed(2) ?? '0.00'}`} />
                          </OrderDetailContainer>

                          <OrderDetailContainer>
                            <OrderInformation label={'Tab Name:'} value={check?.tab_name} />
                            <OrderInformation label={'Tax:'} value={`$${Number(check?.tax).toFixed(2) ?? '0.00'}`} />
                          </OrderDetailContainer>

                          {/* Items */}
                          <div>
                            <StyledOrderSubTitle
                              variant="h6"
                              sx={{
                                fontWeight: '400',
                                marginTop: '35px'
                              }}
                            >
                              Items
                            </StyledOrderSubTitle>
                            <Divider sx={{marginTop: '15px'}}/>
                            <ModalTable
                              columns={tableColumns(itemTableColumns) || []}
                              data={itemTableRows(check?.items) || []}
                              type="log entries"
                              itemsPerPage={1000}
                              pageCount={1000}
                              page={1}
                              report={false}
                              hideNavigation={true}
                            />
                          </div>

                          {/* Perqs */}
                          <div>
                            <StyledOrderSubTitle
                              variant="h6"
                              sx={{
                                fontWeight: '400',
                                marginTop: '35px'
                              }}
                            >
                              Perqs
                            </StyledOrderSubTitle>
                            <Divider sx={{marginTop: '15px'}}/>
                            <ModalTable
                              columns={tableColumns(discountTableColumns) || []}
                              data={discountTableRows(check?.discounts) || []}
                              type="log entries"
                              itemsPerPage={1000}
                              pageCount={1000}
                              page={1}
                              report={false}
                              hideNavigation={true}
                            />
                          </div>

                          {/* Service Charges */}
                          <div>
                            <StyledOrderSubTitle
                              variant="h6"
                              sx={{
                                fontWeight: '400',
                                marginTop: '35px'
                              }}
                            >
                              Service Charges
                            </StyledOrderSubTitle>
                            <Divider sx={{marginTop: '15px'}}/>
                            <ModalTable
                              columns={tableColumns(serviceChargesTableColumns) || []}
                              data={serviceChargesTableRows(check?.service_charges) || []}
                              type="log entries"
                              itemsPerPage={1000}
                              pageCount={1000}
                              page={1}
                              report={false}
                              hideNavigation={true}
                            />
                          </div>

                          {/* Payments */}
                          <div>
                            <StyledOrderSubTitle
                              variant="h6"
                              sx={{
                                fontWeight: '400',
                                marginTop: '35px'
                              }}
                            >
                              Payments
                            </StyledOrderSubTitle>
                            <Divider sx={{marginTop: '15px'}}/>
                            <ModalTable
                              columns={tableColumns(paymentTableColumns) || []}
                              data={paymentTableRows(check?.payments) || []}
                              type="log entries"
                              itemsPerPage={1000}
                              pageCount={1000}
                              page={1}
                              report={false}
                              hideNavigation={true}
                            />
                          </div>
                        </div>
                      )
                    })
                  )
                }
              </DialogContent>
            )
          )
        }
      </Dialog>
    </React.Fragment>
  )
}
